import { useCallback, useEffect, useRef, useState } from "react"

import { CAST_APP_ID, CAST_ENABLED, CAST_NAMESPACE } from "./castConfig"
import type { CastContext, CastFramework } from "./castTypes"

const SENDER_SDK = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1"

/**
 * Casting the room to a television.
 *
 * The Cast **Web** Sender SDK only exists in Chrome-based browsers on desktop
 * and Android. Safari and Chrome-for-iOS have no Cast API at all — casting from
 * an iPhone needs a native app using the iOS Cast SDK, which a web build can't
 * reach. So `available` stays false there and callers are expected to render
 * nothing rather than a button that can't work; the room's QR and URL are the
 * path to a TV on those devices.
 *
 * What actually crosses to the television is one small message: the room code.
 * The receiver is our own /tv page, which then opens its own socket and watches
 * the room like any other display — the phone is a remote control for launching
 * it, never a relay for the game itself.
 */

export type CastState =
    | { kind: "unavailable" }
    | { kind: "idle" }
    | { kind: "connecting" }
    | { kind: "connected"; deviceName: string | null }

let sdkPromise: Promise<CastFramework | null> | null = null

/** Load the sender SDK once per page and resolve with the framework, or null
 *  when this browser has no Cast support. */
function loadSenderSdk(): Promise<CastFramework | null> {
    if (sdkPromise) return sdkPromise

    sdkPromise = new Promise<CastFramework | null>(resolve => {
        if (typeof window === "undefined" || !CAST_ENABLED) return resolve(null)
        if (window.cast?.framework) return resolve(window.cast.framework)

        // The SDK calls this global once it knows whether Cast can run here.
        window.__onGCastApiAvailable = (available: boolean) => {
            resolve(available && window.cast?.framework ? window.cast.framework : null)
        }

        const script = document.createElement("script")
        script.src = SENDER_SDK
        script.async = true
        // A blocked or unreachable gstatic (offline, strict network) must not
        // leave the promise pending forever — callers await it on mount.
        script.onerror = () => resolve(null)
        document.head.appendChild(script)

        // Belt to that braces: if the SDK loads but never calls the callback
        // (older Chrome without a Cast-capable profile), stop waiting.
        setTimeout(() => resolve(window.cast?.framework ?? null), 6000)
    })

    return sdkPromise
}

export function useCastSender(roomCode: string | null) {
    const [state, setState] = useState<CastState>({ kind: "unavailable" })
    const contextRef = useRef<CastContext | null>(null)
    const frameworkRef = useRef<CastFramework | null>(null)
    /** The code we've told the receiver about, so we don't resend on every tick. */
    const sentCodeRef = useRef<string | null>(null)

    /* ── Bring the SDK up and track session state ────────────────────────── */
    useEffect(() => {
        if (!CAST_ENABLED) return
        let cancelled = false

        loadSenderSdk().then(framework => {
            if (cancelled || !framework || !CAST_APP_ID) return

            frameworkRef.current = framework
            const context = framework.CastContext.getInstance()
            contextRef.current = context

            context.setOptions({
                receiverApplicationId: CAST_APP_ID,
                // Origin-scoped so a session started by one tab of the game is
                // picked up by another — refreshing the phone shouldn't orphan
                // the television.
                autoJoinPolicy: window.chrome?.cast?.AutoJoinPolicy.ORIGIN_SCOPED ?? "origin_scoped",
            })

            const sync = () => {
                const castState = context.getCastState()
                const session = context.getCurrentSession()
                if (castState === framework.CastState.CONNECTED && session) {
                    setState({ kind: "connected", deviceName: session.getCastDevice()?.friendlyName ?? null })
                } else if (castState === framework.CastState.CONNECTING) {
                    setState({ kind: "connecting" })
                } else {
                    /*
                     * NO_DEVICES_AVAILABLE is deliberately not treated as "hide".
                     * Chrome doesn't scan the network until the user shows cast
                     * intent, so it reports no devices until they've opened the
                     * browser's own cast menu once — which made our button turn
                     * up only after they'd already found the thing it exists to
                     * save them. An empty picker is Chrome's to explain.
                     */
                    setState({ kind: "idle" })
                }
            }

            context.addEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, sync)
            context.addEventListener(framework.CastContextEventType.SESSION_STATE_CHANGED, sync)
            sync()
        })

        return () => { cancelled = true }
    }, [])

    /* ── Keep the receiver pointed at the current room ───────────────────── */
    useEffect(() => {
        if (state.kind !== "connected" || !roomCode) return
        const session = contextRef.current?.getCurrentSession()
        if (!session) return
        if (sentCodeRef.current === roomCode) return

        session
            .sendMessage(CAST_NAMESPACE, { roomCode })
            .then(() => { sentCodeRef.current = roomCode })
            .catch(error => {
                // The receiver may still be booting when a session first
                // reports connected; leaving sentCode unset means the next
                // state change retries rather than stranding a blank TV.
                console.warn("Cast: could not send the room code", error)
            })
    }, [state, roomCode])

    // A new room means the television is being told something different.
    useEffect(() => { sentCodeRef.current = null }, [roomCode])

    const startCasting = useCallback(() => {
        const context = contextRef.current
        if (!context) return
        // Opens Chrome's own device picker. A user gesture is required, so this
        // must be called straight from a click handler.
        context.requestSession().catch(error => {
            // "cancel" is the user dismissing the picker — not a failure.
            if (String(error) !== "cancel") console.warn("Cast: session request failed", error)
        })
    }, [])

    const stopCasting = useCallback(() => {
        contextRef.current?.endCurrentSession(true)
        sentCodeRef.current = null
    }, [])

    return { state, startCasting, stopCasting }
}
